import { type ReactElement, useLayoutEffect, useRef, useState } from "react"
import { ResponsiveContainer } from "recharts"

type ChartSize = { width: number; height: number }

/** Mounts Recharts only after the host has a real layout box. */
export const PositiveSizeChart = ({ children }: { children: ReactElement }) => {
	const hostRef = useRef<HTMLDivElement>(null)
	const [size, setSize] = useState<ChartSize | null>(null)

	useLayoutEffect(() => {
		const host = hostRef.current
		if (!host) return

		const update = (width: number, height: number) => {
			const next = width > 0 && height > 0
				? { width: Math.floor(width), height: Math.floor(height) }
				: null
			setSize(current =>
				current?.width === next?.width && current?.height === next?.height ? current : next
			)
		}
		const measure = () => {
			const bounds = host.getBoundingClientRect()
			update(bounds.width, bounds.height)
		}

		measure()
		const observer = new ResizeObserver(entries => {
			const entry = entries[0]
			if (entry) update(entry.contentRect.width, entry.contentRect.height)
		})
		observer.observe(host)
		return () => observer.disconnect()
	}, [])

	return (
		<div ref={hostRef} style={{ width: "100%", height: "100%", minWidth: 0, minHeight: 0 }}>
			{size ? (
				<ResponsiveContainer width={size.width} height={size.height}>
					{children}
				</ResponsiveContainer>
			) : null}
		</div>
	)
}
